/**
 * Global search (#50, owner 2026-10-07): one field, always in the same place, that finds a screen, an
 * employee or a team and goes there. ⌘K, Ctrl+K or / opens it from anywhere but a text field.
 * It searches what the person may see and nothing more: screens from the menu they already have, the
 * people only with ansatte.se, the teams only with vaktplan.se. People and teams are read when the
 * field first opens, not on every page.
 */
import { get } from '@/api/client';
import type { ApiTeam, ApiUser } from '@/api/data';
import { type Cap, useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { Icon } from '@/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';

export interface Hit {
  kind: 'festet' | 'nylig' | 'side' | 'ansatt' | 'lag';
  label: string;
  sub?: string | undefined;
  to: string;
}

const norm = (s: string) => s.toLocaleLowerCase('nb').normalize('NFKD').replace(/\p{M}/gu, '');
// tasks only: «lonn» finds Lønn, as people type it without the keys (#272 SS-02); names keep ø as its own letter
const fold = (s: string) => norm(s).replace(/ø/g, 'o').replace(/æ/g, 'ae');

/**
 * Tasks a manager looks for by what they want done, not by the menu's word (#266, V6): each names where it
 * lives and the words people use for it. Shown only with the permission its route asks for.
 */
export interface Task {
  label: string;
  path: string;
  to: string;
  /** null: every member, the employee's own pages */
  cap: Cap | null;
  words: string;
}
export const TASKS: Task[] = [
  {
    label: 'Lønnsgrunnlag',
    path: 'Rapporter → Lønnsgrunnlag',
    to: '/lonn',
    cap: 'timerloenn.se',
    words: 'lønn lønnskjøring eksport fil visma payroll timer til lønn kontrollpunkter',
  },
  {
    label: 'Lønnsprosent',
    path: 'Rapporter → Lønnsprosent',
    to: '/omsetning',
    cap: 'vaktplan.kost',
    words: 'lønn lønnskostnad omsetning salg budsjett lønn prosent kostnad',
  },
  {
    label: 'Deltid',
    path: 'Rapporter → Deltid',
    to: '/deltid',
    cap: 'timerloenn.se',
    words: 'deltid stillingsprosent merarbeid fast ansettelse',
  },
  {
    label: 'Lønn og integrasjoner',
    path: 'Innstillinger → Lønn og integrasjoner',
    to: '/innstillinger/lonn',
    cap: 'innstillinger.se',
    words: 'lønn lønnsart lønnssystem satser tillegg overtid arbeidsgiveravgift feriepenger pensjon',
  },
  {
    label: 'Kontroll',
    path: 'Kontroll → Gjennomgang',
    to: '/timeliste',
    cap: 'timeliste.se',
    words: 'godkjenn godkjenning timeliste timer stempling registreringer avvik rett',
  },
  {
    label: 'Registreringer',
    path: 'Kontroll → Registreringer',
    to: '/timeliste?fane=registreringer',
    cap: 'timeliste.se',
    words: 'registreringer alle stemplinger timer arbeidstid paragraf 10-7',
  },
  {
    label: 'Arbeidstid per person',
    path: 'Kontroll → Per ansatt',
    to: '/arbeidstid?vis=oversikt',
    cap: 'timeliste.se',
    words: 'arbeidstid overtid hvile timer personalliste utskrift',
  },
  {
    label: 'Fravær',
    path: 'Fravær → Søknader',
    to: '/fravaer/soknader',
    cap: 'fravaer.se',
    words: 'ferie syk sykdom permisjon fri søknad saldo',
  },
  {
    label: 'Vaktplan',
    path: 'Vaktplan',
    to: '/vaktplan',
    cap: 'vaktplan.se',
    words: 'plan turnus vakter uke publiser ledig vakt',
  },
  {
    label: 'Ansatte',
    path: 'Ansatte',
    to: '/ansatte',
    cap: 'timeliste.se', // the sidebar's Ansatte is a manager's; ansatte.se alone is every member (#272 SS-22)
    words: 'ansatt ansattnummer kontrakt invitasjon',
  },
  {
    label: 'Bemanning',
    path: 'Innstillinger → Bemanning',
    to: '/innstillinger/bemanning',
    cap: 'innstillinger.se',
    words: 'bemanning behov åpningstid',
  },
  {
    label: 'Pause og arbeidstid',
    path: 'Innstillinger → Pause',
    to: '/innstillinger/pause',
    cap: 'innstillinger.se',
    words: 'pause lunsj pauseregel ubetalt',
  },
  {
    label: 'Koble til Tripletex eller PowerOffice',
    path: 'Innstillinger → Lønn og integrasjoner → Kobling',
    to: '/innstillinger/lonn#integrasjon',
    cap: 'innstillinger.se',
    words: 'tripletex poweroffice integrasjon koble kobling api nøkkel',
  },
  {
    label: 'Min plan',
    path: 'Personlig → Min plan',
    to: '/min/plan',
    cap: null,
    words: 'mine vakter plan uke',
  },
  {
    label: 'Mitt fravær',
    path: 'Personlig → Mitt fravær',
    to: '/min/fravaer',
    cap: null,
    words: 'ferie syk fri søknad',
  },
  {
    label: 'Roller og tilgang',
    path: 'Innstillinger → Roller',
    to: '/innstillinger/roller',
    cap: 'innstillinger.se',
    words: 'rolle tilgang rettigheter leder',
  },
  {
    label: 'Endringslogg',
    path: 'Innstillinger → Tilgang og historikk → Endringslogg',
    to: '/innstillinger/endringer',
    cap: 'roller.tildele', // the administrator's, as the server's GET /settings/history (#267 SET-32)
    words: 'endringslogg historikk logg endret hvem endret innstillinger revisjon',
  },
];
const MAX = 6;

/** The hits for a query, grouped as screens, people, teams, at most six of each. Pure, for the tests. */
export function search(
  q: string,
  pages: { to: string; label: string }[],
  people: ApiUser[],
  teams: ApiTeam[],
  tasks: Task[] = [],
): Hit[] {
  const n = norm(q.trim());
  if (!n) return [];
  const has = (s: string | null | undefined) => !!s && norm(s).includes(n);
  // tasks first, by name or by a word people use for them, then the menu's own screens not already found
  // the name first, then a whole word people use, then part of a word: «ferie» is Fravær before feriepenger
  const f = fold(q.trim());
  const score = (t: Task) => {
    if (fold(t.label).startsWith(f)) return 4;
    if (fold(t.label).includes(f) || fold(t.path).includes(f)) return 3;
    const words = fold(t.words).split(/\s+/);
    if (words.includes(f)) return 2;
    return words.some((w) => w.includes(f)) ? 1 : 0;
  };
  const task = tasks
    .map((t) => ({ t, sc: score(t) }))
    .filter((x) => x.sc > 0)
    .sort((x, y) => y.sc - x.sc)
    .map(({ t }): Hit => ({ kind: 'side', label: t.label, sub: t.path, to: t.to }));
  const seen = new Set(task.map((h) => h.to));
  const side = [
    ...task,
    ...pages
      .filter((p) => has(p.label) && !seen.has(p.to))
      .map((p): Hit => ({ kind: 'side', label: p.label, to: p.to })),
  ];
  const ansatt = people
    .filter((u) => u.active_status !== false && has(`${u.name} ${u.lastname ?? ''}`))
    .map(
      (u): Hit => ({
        kind: 'ansatt',
        label: `${u.name} ${u.lastname ?? ''}`.trim(),
        sub: u.title ?? undefined,
        to: `/ansatte/${u.id}`,
      }),
    );
  const lag = teams
    .filter((t) => has(t.name))
    .map(
      (t): Hit => ({ kind: 'lag', label: t.name, sub: T('Vaktplan for laget'), to: `/vaktplan?lag=${t.id}` }),
    );
  return [...side.slice(0, MAX), ...ansatt.slice(0, MAX), ...lag.slice(0, MAX)];
}

/** the menu's icon for where a task lives */
const iconOf = (to: string) =>
  to.startsWith('/lonn') || to.startsWith('/deltid')
    ? ('banknote' as const)
    : to.startsWith('/omsetning')
      ? ('percent' as const)
      : to.startsWith('/timeliste') || to.startsWith('/arbeidstid')
        ? ('clock' as const)
        : to.startsWith('/fravaer') || to.startsWith('/min/fravaer')
          ? ('sun' as const)
          : to.startsWith('/vaktplan') || to.startsWith('/min/plan')
            ? ('calendar' as const)
            : to.startsWith('/ansatte')
              ? ('users' as const)
              : to.startsWith('/innstillinger')
                ? ('sliders' as const)
                : ('document' as const);

const GROUP: Record<Hit['kind'], string> = {
  festet: 'Festet',
  nylig: 'Sist brukt',
  side: 'Oppgaver og sider',
  ansatt: 'Ansatte',
  lag: 'Lag',
};

/** the last places opened from search, in this browser only: a convenience, never relied on (#272 SS-06) */
const RECENT = 'timefly.search.recent';
const readRecent = (): string[] => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT) ?? '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, 3) : [];
  } catch {
    return [];
  }
};
/** tasks the manager pinned, first in the empty field (V6 search): per browser, like the recent list */
const PINS = 'timefly.search.pins';
const readPins = (): string[] => {
  try {
    const v = JSON.parse(localStorage.getItem(PINS) ?? '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, 5) : [];
  } catch {
    return [];
  }
};
const writePins = (pins: string[]) => {
  try {
    localStorage.setItem(PINS, JSON.stringify(pins.slice(0, 5)));
  } catch {
    // private mode or blocked storage: pins last for this visit only
  }
};
const keepRecent = (to: string) => {
  try {
    localStorage.setItem(RECENT, JSON.stringify([to, ...readRecent().filter((x) => x !== to)].slice(0, 3)));
  } catch {
    // private mode or blocked storage: search works without it
  }
};

/** One dialog for the app, opened from either button (the sidebar's and the phone bar's) or a key. */
export const useSearchOpen = create<{ open: boolean; setOpen: (o: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

/** The field under the sidebar brand, or the magnifier in the phone bar (`compact`): both open the one dialog. */
export function SearchButton({ compact = false, top = false }: { compact?: boolean; top?: boolean }) {
  const setOpen = useSearchOpen((s) => s.setOpen);
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <button
      type="button"
      className={compact ? 'bell' : top ? 'gsearch gsearch-top' : 'gsearch'}
      onClick={() => setOpen(true)}
      aria-label={T('Søk i Timefly')}
      data-testid={compact ? 'search-open-phone' : top ? 'search-open-top' : 'search-open'}
    >
      <Icon name="search" />
      {!compact && (
        <>
          <span>{top ? T('Søk i alt') : T('Søk')}</span>
          <kbd>{mac ? '⌘K' : 'Ctrl K'}</kbd>
        </>
      )}
    </button>
  );
}

/** The dialog and the keys that open it; rendered once, by the shell. */
export function GlobalSearch({ pages }: { pages: { to: string; label: string }[] }) {
  const can = useCan();
  const nav = useNavigate();
  const { open, setOpen } = useSearchOpen();
  const [q, setQ] = useState('');
  const [at, setAt] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const dlg = useRef<HTMLDialogElement>(null);
  // focus goes back where it was when the search closes (#272 M-06)
  const opener = useRef<HTMLElement | null>(null);
  const seePeople = can('ansatte.se');
  const seeTeams = can('vaktplan.se');
  const [asked, setAsked] = useState(false);
  const people = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const r = await get<ApiUser[] | { users: ApiUser[] }>('/users');
      return Array.isArray(r) ? r : r.users;
    },
    enabled: asked && seePeople,
    staleTime: 60_000,
  });
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => get<ApiTeam[]>('/admin/teams'),
    enabled: asked && seeTeams,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing =
        e.target instanceof HTMLElement &&
        e.target.closest('input, textarea, select, [contenteditable=true]');
      // a page that took «/» for its own search (Vaktplan's «Finn ansatt») has prevented it
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing && !e.defaultPrevented)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [setOpen]);
  useEffect(() => {
    if (!open) {
      opener.current?.focus();
      opener.current = null;
      return;
    }
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setAsked(true);
    setQ('');
    setAt(0);
    input.current?.focus();
  }, [open]);

  const mine = useMemo(
    () =>
      TASKS.filter((t) => t.cap == null || can(t.cap)).map((t) => ({
        ...t,
        label: T(t.label),
        path: T(t.path),
      })),
    [can],
  );
  const found = useMemo(
    () => search(q, pages, seePeople ? (people.data ?? []) : [], seeTeams ? (teams.data ?? []) : [], mine),
    [q, pages, people.data, teams.data, seePeople, seeTeams, mine],
  );
  // nothing typed: the common tasks, so the field is never a blank box (#272 SS-06)
  const manager = can('timeliste.se');
  const ORDER = manager
    ? ['/timeliste', '/lonn', '/vaktplan', '/fravaer/soknader', '/omsetning', '/innstillinger/lonn']
    : ['/min/plan', '/min/fravaer'];
  const [pins, setPins] = useState<string[]>(readPins);
  const togglePin = (to: string) => {
    requestAnimationFrame(() =>
      dlg.current?.querySelector<HTMLButtonElement>(`button.gspin[data-to="${CSS.escape(to)}"]`)?.focus(),
    );
    const next = pins.includes(to) ? pins.filter((x) => x !== to) : [to, ...pins].slice(0, 5);
    setPins(next);
    writePins(next);
  };
  const pinHits = pins
    .map((to) => mine.find((t) => t.to === to))
    .filter((t): t is (typeof mine)[number] => !!t)
    .map((t): Hit => ({ kind: 'festet', label: t.label, sub: t.path, to: t.to }));
  const recent = open ? readRecent().filter((to) => !pins.includes(to)) : [];
  const recentHits = recent
    .map((to) => mine.find((t) => t.to === to))
    .filter((t): t is (typeof mine)[number] => !!t)
    .map((t): Hit => ({ kind: 'nylig', label: t.label, sub: t.path, to: t.to }));
  const hits = q.trim()
    ? found
    : [
        ...pinHits,
        ...recentHits,
        ...ORDER.filter((to) => !recent.includes(to) && !pins.includes(to))
          .map((to) => mine.find((t) => t.to === to))
          .filter((t): t is (typeof mine)[number] => !!t)
          .map((t): Hit => ({ kind: 'side', label: t.label, sub: t.path, to: t.to })),
      ];
  const kinds = new Set(hits.map((h) => h.kind)).size;
  const go = (h: Hit | undefined) => {
    if (!h) return;
    if (h.kind === 'side' || h.kind === 'nylig' || h.kind === 'festet') keepRecent(h.to);
    // focus goes to the destination, not back to the trigger, where the same Enter would reopen the search (#274 R03)
    opener.current = null;
    setOpen(false);
    nav(h.to);
    requestAnimationFrame(() => document.getElementById('main')?.focus());
  };
  return (
    <>
      {open && (
        <div className="confirm" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
          <dialog
            open
            ref={dlg}
            aria-label={T('Søk i Timefly')}
            aria-modal="true"
            className="gsdlg"
            data-testid="search"
            onKeyDown={(e) => {
              // a modal: Escape anywhere closes it, Tab stays inside (#272 M-06)
              if (e.key === 'Escape') {
                e.preventDefault();
                setOpen(false);
              } else if (e.key === 'Tab') {
                const f = [
                  ...(dlg.current?.querySelectorAll<HTMLElement>('input, button:not([tabindex="-1"])') ?? []),
                ];
                const first = f[0];
                const last = f[f.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                  e.preventDefault();
                  last?.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                  e.preventDefault();
                  first?.focus();
                }
              }
            }}
          >
            <div className="gshead">
              <Icon name="search" />
              <input
                ref={input}
                type="search"
                aria-label={T('Søk i Timefly')}
                placeholder={T('Oppgave, ansatt eller lag')}
                aria-controls="gslist"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setAt(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setAt((i) => Math.min(i + 1, hits.length - 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setAt((i) => Math.max(i - 1, 0));
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    go(hits[at]);
                  }
                }}
              />
              <button
                type="button"
                className="gsesc"
                onClick={() => setOpen(false)}
                aria-label={T('Lukk søket')}
              >
                <span className="gsesck">Esc</span>
                <Icon name="close" />
              </button>
            </div>
            {q.trim() && hits.length > 0 && (
              <output className="gscount">
                {hits.length === 1 ? T('1 treff') : T('{n} treff', { n: hits.length })}
              </output>
            )}
            {/* the arrow keys' choice, read out: the list stays plain buttons (#272 SS-01) */}
            <output className="sr" aria-live="polite">
              {hits[at]
                ? `${hits[at].label}${hits[at].sub ? `, ${hits[at].sub}` : ''}, ${at + 1} ${T('av')} ${hits.length}`
                : ''}
            </output>
            <ul className="gslist" id="gslist" aria-label={T('Treff')}>
              {hits.map((h, i) => (
                <li key={`${h.kind}-${h.to}`}>
                  {/* a heading only where there is more than one kind of hit (#272 SS-12) */}
                  {kinds > 1 && (i === 0 || hits[i - 1]?.kind !== h.kind) && (
                    <small className="gsgrp">{T(GROUP[h.kind])}</small>
                  )}
                  <button
                    type="button"
                    id={`gs-${i}`}
                    tabIndex={-1}
                    className={i === at ? 'on' : ''}
                    onMouseEnter={() => setAt(i)}
                    onClick={() => go(h)}
                  >
                    <Icon name={h.kind === 'ansatt' ? 'user' : h.kind === 'lag' ? 'users' : iconOf(h.to)} />
                    <span className="gst">
                      <b>{h.label}</b>
                      {h.sub && <span>{h.sub}</span>}
                    </span>
                    <span className="gsgo" aria-hidden="true">
                      →
                    </span>
                  </button>
                  {(h.kind === 'side' || h.kind === 'nylig' || h.kind === 'festet') &&
                    TASKS.some((t) => t.to === h.to) && (
                      <button
                        type="button"
                        className={`gspin${pins.includes(h.to) ? ' on' : ''}`}
                        data-to={h.to}
                        aria-pressed={pins.includes(h.to)}
                        aria-label={
                          pins.includes(h.to)
                            ? T('Løsne {name}', { name: h.label })
                            : T('Fest {name}', { name: h.label })
                        }
                        onClick={() => togglePin(h.to)}
                      >
                        <Icon name="pushpin" />
                      </button>
                    )}
                </li>
              ))}
            </ul>
            {q.trim() && !hits.length && (
              <p className="gsnone">
                {T('Ingen treff for «{q}». Prøv et annet ord eller et navn.', { q: q.trim() })}
              </p>
            )}
            <p className="gshint">{T('Søk i oppgaver, ansatte og lag. Piltastene velger, Enter åpner.')}</p>
          </dialog>
        </div>
      )}
    </>
  );
}
